(() => {
  if (window.__nueVariantImageLoadingOptimizer) return;
  window.__nueVariantImageLoadingOptimizer = true;

  const preloadedUrls = new Map();
  const enhancedPickers = new WeakSet();
  const enhancedCardSwatches = new WeakSet();

  const setFetchPriority = (image, priority) => {
    try {
      image.fetchPriority = priority;
    } catch (error) {
      // Browsers without fetchPriority support simply ignore it.
    }
  };

  const toLargerShopifyImage = (source, width = 1200) => {
    if (!source || source.startsWith('data:') || source.startsWith('blob:')) return source || '';

    try {
      const url = new URL(source, window.location.href);
      const isShopifyImage =
        url.hostname.includes('cdn.shopify.com') ||
        url.hostname.includes('shopifycdn.com') ||
        url.searchParams.has('width');

      if (isShopifyImage) {
        url.searchParams.set('width', String(width));
        return url.toString();
      }

      return url.toString();
    } catch (error) {
      return source;
    }
  };

  const preloadUrl = (source, priority = 'low', width = 1200) => {
    const url = toLargerShopifyImage(source, width);
    if (!url) return Promise.resolve();
    if (preloadedUrls.has(url)) return preloadedUrls.get(url);

    const promise = new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      setFetchPriority(image, priority);
      image.onload = async () => {
        try {
          await image.decode?.();
        } catch (error) {
          // The image is still usable when decode is unavailable or rejects.
        }
        resolve();
      };
      image.onerror = resolve;
      image.src = url;
      if (image.complete) queueMicrotask(resolve);
    });

    preloadedUrls.set(url, promise);
    return promise;
  };

  const getImageSource = (image) => {
    if (!(image instanceof HTMLImageElement)) return '';
    return image.currentSrc || image.src || image.dataset.src || '';
  };

  const activateImage = (image, priority = 'auto') => {
    if (!(image instanceof HTMLImageElement)) return Promise.resolve();

    image.loading = 'eager';
    image.decoding = 'async';
    setFetchPriority(image, priority);

    if (!image.src && image.dataset.src) image.src = image.dataset.src;
    if (!image.srcset && image.dataset.srcset) image.srcset = image.dataset.srcset;

    if (!getImageSource(image)) return Promise.resolve();

    return image.complete
      ? Promise.resolve()
      : new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
  };

  const primeVariantSample = (sample, priority = 'high') => {
    if (!(sample instanceof Element)) return Promise.resolve();

    const image = sample.matches('img')
      ? sample
      : sample.querySelector('.variant-option__sample-image, img');

    if (!(image instanceof HTMLImageElement)) return Promise.resolve();
    activateImage(image, priority);
    return preloadUrl(getImageSource(image), priority, 1400);
  };

  const enhanceVariantPicker = (picker) => {
    if (!(picker instanceof Element) || enhancedPickers.has(picker)) return;
    enhancedPickers.add(picker);

    const sampleImages = Array.from(
      picker.querySelectorAll(
        '.variant-option__sample-image, .variant-option__sample-card img, .variant-option__select-wrapper img'
      )
    );

    sampleImages.forEach((image, index) => {
      const selected = Boolean(image.closest('.variant-option__sample-card')?.querySelector('input:checked'));
      activateImage(image, selected ? 'high' : index < 6 ? 'auto' : 'low');
    });

    const samples = Array.from(picker.querySelectorAll('.variant-option__sample-card'));

    picker.querySelectorAll('[data-option-value-id]').forEach((option) => {
      if (option.querySelector('img') && !samples.includes(option)) samples.push(option);
    });

    const selectedSample = samples.find((sample) => sample.querySelector('input:checked'));
    if (selectedSample) primeVariantSample(selectedSample, 'high');

    samples.slice(0, 4).forEach((sample, index) => {
      if (sample === selectedSample) return;
      window.setTimeout(() => primeVariantSample(sample, index === 0 ? 'auto' : 'low'), 50 + index * 70);
    });
  };

  const preloadTargetProductCardImage = (button, priority = 'high') => {
    if (!(button instanceof HTMLElement)) return Promise.resolve();

    const card = button.closest('product-card');
    const mediaId = button.dataset.mediaId;
    const slideshow = card?.querySelector('slideshow-component');
    const slide = mediaId
      ? slideshow?.querySelector(`slideshow-slide[slide-id="${CSS.escape(mediaId)}"]`)
      : null;
    const slideImage = slide?.querySelector('img');

    if (slideImage instanceof HTMLImageElement) {
      activateImage(slideImage, priority);
      return preloadUrl(getImageSource(slideImage), priority, 1200);
    }

    return preloadUrl(button.dataset.preloadUrl || button.dataset.imageUrl, priority, 1200);
  };

  const enhanceCardSwatches = (component) => {
    if (!(component instanceof Element) || enhancedCardSwatches.has(component)) return;
    enhancedCardSwatches.add(component);

    const buttons = Array.from(component.querySelectorAll('[data-nue-card-swatch]'));
    if (!buttons.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        buttons.slice(0, 3).forEach((button, index) => {
          window.setTimeout(
            () => preloadTargetProductCardImage(button, index === 0 ? 'auto' : 'low'),
            index * 90
          );
        });

        observer.disconnect();
      },
      { rootMargin: '500px 0px' }
    );

    observer.observe(component);
  };

  const scan = (root = document) => {
    if (root instanceof Element && root.matches('variant-picker')) enhanceVariantPicker(root);
    root.querySelectorAll?.('variant-picker').forEach(enhanceVariantPicker);

    if (root instanceof Element && root.matches('nue-card-option-swatches')) enhanceCardSwatches(root);
    root.querySelectorAll?.('nue-card-option-swatches').forEach(enhanceCardSwatches);
  };

  const findInteractiveTarget = (target) => {
    if (!(target instanceof Element)) return null;
    return target.closest(
      '.variant-option__sample-card, .variant-option__select-wrapper [role="option"], [data-nue-card-swatch]'
    );
  };

  const primeInteractiveTarget = (event) => {
    const target = findInteractiveTarget(event.target);
    if (!target) return;

    if (target.matches('[data-nue-card-swatch]')) {
      preloadTargetProductCardImage(target, 'high');
    } else {
      primeVariantSample(target, 'high');
    }
  };

  document.addEventListener('pointerover', primeInteractiveTarget, { passive: true });
  document.addEventListener('pointerdown', primeInteractiveTarget, { passive: true });
  document.addEventListener('touchstart', primeInteractiveTarget, { passive: true });
  document.addEventListener('focusin', primeInteractiveTarget);

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-nue-card-swatch]') : null;
      if (!button) return;

      const mediaId = button.dataset.mediaId;
      const slideshow = button.closest('product-card')?.querySelector('slideshow-component');
      const slide = mediaId
        ? slideshow?.querySelector(`slideshow-slide[slide-id="${CSS.escape(mediaId)}"]`)
        : null;
      const image = slide?.querySelector('img');

      if (image instanceof HTMLImageElement) activateImage(image, 'high');
      preloadTargetProductCardImage(button, 'high');
    },
    true
  );

  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
